// ============================================================
// GoldRatesWebsite.js  — updated with CountrySidebar
// Changes from previous version:
//   1. Import CountrySidebar
//   2. Added `activeCurrency` state
//   3. Home page layout wrapped in flex row: main content + sidebar
//   4. Cards scroll-highlight when region/currency clicked from sidebar
//   5. Clicking a country in sidebar scrolls to + highlights that card
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, DollarSign, Globe, Info, RefreshCw, AlertCircle, Shield, Mail  } from 'lucide-react';
import CountrySidebar from './CountrySidebar';
import { GOLD_URL_MAPPINGS } from './goldUrlMappings';
import BlogWidget from './components/BlogWidget';

const GoldRatesWebsite = ({ initialPage = 'home' }) => {
  const navigate = useNavigate();

  const API_BASE_URL = 'https://livepriceforgold-backend.vercel.app/api';
   

  const [searchTerm, setSearchTerm]       = useState('');
  const [sortBy, setSortBy]               = useState('country');
  const [selectedKarat, setSelectedKarat] = useState('24');
  const [selectedUnit, setSelectedUnit]   = useState('toz');
  const [goldData, setGoldData]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [lastUpdated, setLastUpdated]     = useState(null);
  const [securityWarning, setSecurityWarning] = useState(false);
  const [currentPage, setCurrentPage]     = useState(initialPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    if (page === 'home') navigate('/');
    else navigate('/' + page);
  };

  useEffect(() => {
    let devToolsOpen = false;
    const detectDevTools = () => {
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        if (!devToolsOpen) { devToolsOpen = true; setSecurityWarning(true); setTimeout(() => setSecurityWarning(false), 5000); }
      } else { devToolsOpen = false; }
    };
    const interval = setInterval(detectDevTools, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .gold-card { transition: box-shadow 0.3s, transform 0.3s, ring 0.3s; }
      .gold-card::after { content: "© Global Gold Rates - Authorized Use Only"; position: absolute; opacity: 0; pointer-events: none; }
      @media print { .no-print { display: none !important; } }
      /* Custom scrollbar for sidebar */
      .scrollbar-thin::-webkit-scrollbar { width: 4px; }
      .scrollbar-thin::-webkit-scrollbar-track { background: #fef3c7; }
      .scrollbar-thin::-webkit-scrollbar-thumb { background: #f59e0b; border-radius: 2px; }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const karatPurity = {
    '24': { purity: 1.0,    label: '24K (99.9% Pure)' },
    '22': { purity: 0.9167, label: '22K (91.67% Pure)' },
    '21': { purity: 0.875,  label: '21K (87.5% Pure)' },
    '18': { purity: 0.75,   label: '18K (75% Pure)' },
    '14': { purity: 0.5833, label: '14K (58.33% Pure)' },
    '10': { purity: 0.4167, label: '10K (41.67% Pure)' },
    '8':  { purity: 0.3333, label: '8K (33.33% Pure)' }
  };

  const weightUnits = {
    'toz':  { label: 'Troy Ounce', symbol: 'oz',   code: 'toz' },
    'g':    { label: 'Gram',       symbol: 'g',    code: 'g' },
    'kg':   { label: 'Kilogram',   symbol: 'kg',   code: 'kg' },
    'mt':   { label: 'Metric Tonne', symbol: 'MT', code: 'mt' },
    'tola': { label: 'Tola',       symbol: 'tola', code: 'tola' }
  };

  const countryInfo = {
    'USD': { country: 'United States', flag: '🇺🇸' },
    'EUR': { country: 'European Union', flag: '🇪🇺' },
    'GBP': { country: 'United Kingdom', flag: '🇬🇧' },
    'JPY': { country: 'Japan', flag: '🇯🇵' },
    'CHF': { country: 'Switzerland', flag: '🇨🇭' },
    'CAD': { country: 'Canada', flag: '🇨🇦' },
    'AUD': { country: 'Australia', flag: '🇦🇺' },
    'NZD': { country: 'New Zealand', flag: '🇳🇿' },
    'CNY': { country: 'China', flag: '🇨🇳' },
    'CNH': { country: 'China (Offshore)', flag: '🇨🇳' },
    'INR': { country: 'India', flag: '🇮🇳' },
    'KRW': { country: 'South Korea', flag: '🇰🇷' },
    'SGD': { country: 'Singapore', flag: '🇸🇬' },
    'HKD': { country: 'Hong Kong', flag: '🇭🇰' },
    'MYR': { country: 'Malaysia', flag: '🇲🇾' },
    'IDR': { country: 'Indonesia', flag: '🇮🇩' },
    'PHP': { country: 'Philippines', flag: '🇵🇭' },
    'THB': { country: 'Thailand', flag: '🇹🇭' },
    'VND': { country: 'Vietnam', flag: '🇻🇳' },
    'PKR': { country: 'Pakistan', flag: '🇵🇰' },
    'BDT': { country: 'Bangladesh', flag: '🇧🇩' },
    'LKR': { country: 'Sri Lanka', flag: '🇱🇰' },
    'NPR': { country: 'Nepal', flag: '🇳🇵' },
    'MMK': { country: 'Myanmar', flag: '🇲🇲' },
    'KHR': { country: 'Cambodia', flag: '🇰🇭' },
    'LAK': { country: 'Laos', flag: '🇱🇦' },
    'BND': { country: 'Brunei', flag: '🇧🇳' },
    'MOP': { country: 'Macau', flag: '🇲🇴' },
    'TWD': { country: 'Taiwan', flag: '🇹🇼' },
    'NTD': { country: 'Taiwan (New Dollar)', flag: '🇹🇼' },
    'MNT': { country: 'Mongolia', flag: '🇲🇳' },
    'KZT': { country: 'Kazakhstan', flag: '🇰🇿' },
    'UZS': { country: 'Uzbekistan', flag: '🇺🇿' },
    'TJS': { country: 'Tajikistan', flag: '🇹🇯' },
    'KGS': { country: 'Kyrgyzstan', flag: '🇰🇬' },
    'TMT': { country: 'Turkmenistan', flag: '🇹🇲' },
    'AFN': { country: 'Afghanistan', flag: '🇦🇫' },
    'BTN': { country: 'Bhutan', flag: '🇧🇹' },
    'MVR': { country: 'Maldives', flag: '🇲🇻' },
    'KPW': { country: 'North Korea', flag: '🇰🇵' },
    'AED': { country: 'United Arab Emirates', flag: '🇦🇪' },
    'SAR': { country: 'Saudi Arabia', flag: '🇸🇦' },
    'QAR': { country: 'Qatar', flag: '🇶🇦' },
    'KWD': { country: 'Kuwait', flag: '🇰🇼' },
    'BHD': { country: 'Bahrain', flag: '🇧🇭' },
    'OMR': { country: 'Oman', flag: '🇴🇲' },
    'JOD': { country: 'Jordan', flag: '🇯🇴' },
    'LBP': { country: 'Lebanon', flag: '🇱🇧' },
    'SYP': { country: 'Syria', flag: '🇸🇾' },
    'IQD': { country: 'Iraq', flag: '🇮🇶' },
    'IRR': { country: 'Iran', flag: '🇮🇷' },
    'YER': { country: 'Yemen', flag: '🇾🇪' },
    'GEL': { country: 'Georgia', flag: '🇬🇪' },
    'AMD': { country: 'Armenia', flag: '🇦🇲' },
    'AZN': { country: 'Azerbaijan', flag: '🇦🇿' },
    'TRY': { country: 'Turkey', flag: '🇹🇷' },
    'NOK': { country: 'Norway', flag: '🇳🇴' },
    'SEK': { country: 'Sweden', flag: '🇸🇪' },
    'DKK': { country: 'Denmark', flag: '🇩🇰' },
    'ISK': { country: 'Iceland', flag: '🇮🇸' },
    'PLN': { country: 'Poland', flag: '🇵🇱' },
    'CZK': { country: 'Czech Republic', flag: '🇨🇿' },
    'HUF': { country: 'Hungary', flag: '🇭🇺' },
    'RON': { country: 'Romania', flag: '🇷🇴' },
    'BGN': { country: 'Bulgaria', flag: '🇧🇬' },
    'HRK': { country: 'Croatia', flag: '🇭🇷' },
    'RSD': { country: 'Serbia', flag: '🇷🇸' },
    'BAM': { country: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    'MKD': { country: 'North Macedonia', flag: '🇲🇰' },
    'ALL': { country: 'Albania', flag: '🇦🇱' },
    'RUB': { country: 'Russia', flag: '🇷🇺' },
    'UAH': { country: 'Ukraine', flag: '🇺🇦' },
    'BYN': { country: 'Belarus', flag: '🇧🇾' },
    'BYR': { country: 'Belarus (Old)', flag: '🇧🇾' },
    'MDL': { country: 'Moldova', flag: '🇲🇩' },
    'ZAR': { country: 'South Africa', flag: '🇿🇦' },
    'EGP': { country: 'Egypt', flag: '🇪🇬' },
    'NGN': { country: 'Nigeria', flag: '🇳🇬' },
    'KES': { country: 'Kenya', flag: '🇰🇪' },
    'TZS': { country: 'Tanzania', flag: '🇹🇿' },
    'UGX': { country: 'Uganda', flag: '🇺🇬' },
    'GHS': { country: 'Ghana', flag: '🇬🇭' },
    'ETB': { country: 'Ethiopia', flag: '🇪🇹' },
    'MAD': { country: 'Morocco', flag: '🇲🇦' },
    'TND': { country: 'Tunisia', flag: '🇹🇳' },
    'DZD': { country: 'Algeria', flag: '🇩🇿' },
    'LYD': { country: 'Libya', flag: '🇱🇾' },
    'SDG': { country: 'Sudan', flag: '🇸🇩' },
    'AOA': { country: 'Angola', flag: '🇦🇴' },
    'BWP': { country: 'Botswana', flag: '🇧🇼' },
    'MZN': { country: 'Mozambique', flag: '🇲🇿' },
    'ZMW': { country: 'Zambia', flag: '🇿🇲' },
    'ZWL': { country: 'Zimbabwe', flag: '🇿🇼' },
    'NAD': { country: 'Namibia', flag: '🇳🇦' },
    'SZL': { country: 'Eswatini', flag: '🇸🇿' },
    'LSL': { country: 'Lesotho', flag: '🇱🇸' },
    'MWK': { country: 'Malawi', flag: '🇲🇼' },
    'RWF': { country: 'Rwanda', flag: '🇷🇼' },
    'BIF': { country: 'Burundi', flag: '🇧🇮' },
    'DJF': { country: 'Djibouti', flag: '🇩🇯' },
    'SOS': { country: 'Somalia', flag: '🇸🇴' },
    'ERN': { country: 'Eritrea', flag: '🇪🇷' },
    'MRU': { country: 'Mauritania', flag: '🇲🇷' },
    'SLL': { country: 'Sierra Leone', flag: '🇸🇱' },
    'LRD': { country: 'Liberia', flag: '🇱🇷' },
    'GMD': { country: 'Gambia', flag: '🇬🇲' },
    'GNF': { country: 'Guinea', flag: '🇬🇳' },
    'CVE': { country: 'Cape Verde', flag: '🇨🇻' },
    'XOF': { country: 'West African CFA', flag: '🌍' },
    'XAF': { country: 'Central African CFA', flag: '🌍' },
    'KMF': { country: 'Comoros', flag: '🇰🇲' },
    'MUR': { country: 'Mauritius', flag: '🇲🇺' },
    'SCR': { country: 'Seychelles', flag: '🇸🇨' },
    'MGA': { country: 'Madagascar', flag: '🇲🇬' },
    'CDF': { country: 'Congo (DRC)', flag: '🇨🇩' },
    'MXN': { country: 'Mexico', flag: '🇲🇽' },
    'GTQ': { country: 'Guatemala', flag: '🇬🇹' },
    'HNL': { country: 'Honduras', flag: '🇭🇳' },
    'NIO': { country: 'Nicaragua', flag: '🇳🇮' },
    'CRC': { country: 'Costa Rica', flag: '🇨🇷' },
    'PAB': { country: 'Panama', flag: '🇵🇦' },
    'BZD': { country: 'Belize', flag: '🇧🇿' },
    'SVC': { country: 'El Salvador', flag: '🇸🇻' },
    'JMD': { country: 'Jamaica', flag: '🇯🇲' },
    'TTD': { country: 'Trinidad and Tobago', flag: '🇹🇹' },
    'BSD': { country: 'Bahamas', flag: '🇧🇸' },
    'BBD': { country: 'Barbados', flag: '🇧🇧' },
    'XCD': { country: 'East Caribbean Dollar', flag: '🌴' },
    'HTG': { country: 'Haiti', flag: '🇭🇹' },
    'DOP': { country: 'Dominican Republic', flag: '🇩🇴' },
    'CUP': { country: 'Cuba', flag: '🇨🇺' },
    'KYD': { country: 'Cayman Islands', flag: '🇰🇾' },
    'BMD': { country: 'Bermuda', flag: '🇧🇲' },
    'AWG': { country: 'Aruba', flag: '🇦🇼' },
    'BRL': { country: 'Brazil', flag: '🇧🇷' },
    'ARS': { country: 'Argentina', flag: '🇦🇷' },
    'CLP': { country: 'Chile', flag: '🇨🇱' },
    'COP': { country: 'Colombia', flag: '🇨🇴' },
    'PEN': { country: 'Peru', flag: '🇵🇪' },
    'VES': { country: 'Venezuela', flag: '🇻🇪' },
    'UYU': { country: 'Uruguay', flag: '🇺🇾' },
    'PYG': { country: 'Paraguay', flag: '🇵🇾' },
    'BOB': { country: 'Bolivia', flag: '🇧🇴' },
    'GYD': { country: 'Guyana', flag: '🇬🇾' },
    'SRD': { country: 'Suriname', flag: '🇸🇷' },
    'FJD': { country: 'Fiji', flag: '🇫🇯' },
    'PGK': { country: 'Papua New Guinea', flag: '🇵🇬' },
    'SBD': { country: 'Solomon Islands', flag: '🇸🇧' },
    'VUV': { country: 'Vanuatu', flag: '🇻🇺' },
    'WST': { country: 'Samoa', flag: '🇼🇸' },
    'TOP': { country: 'Tonga', flag: '🇹🇴' },
    'XPF': { country: 'CFP Franc (Pacific)', flag: '🌏' },
    'GIP': { country: 'Gibraltar', flag: '🇬🇮' },
    'IMP': { country: 'Isle of Man', flag: '🇮🇲' },
  };

   

 

  const fetchGoldRates = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/gold-rates?unit=${selectedUnit}`);
      if (!response.ok) throw new Error(`Backend API error: ${response.status}`);
      const result = await response.json();
      if (result.success && result.data) {
        setGoldData({ goldPrice: result.data.goldPrice, rates: result.data.currencies || {}, unit: selectedUnit });
        setLastUpdated(new Date()); setError(null);
        const payload = { val: result.data.goldPrice, map: result.data.currencies, ts: Date.now() };
        sessionStorage.setItem('_u_data_x', btoa(JSON.stringify(payload) + "|K7_v92_Xp"));
      } else throw new Error('Invalid API response format');
    } catch (err) {
      console.error('Error fetching gold rates:', err); setError(err.message);
      const fallbackPrice = selectedUnit === 'g' ? 84.80 : selectedUnit === 'kg' ? 84800 : selectedUnit === 'mt' ? 84800000 : 2638.50;
      setGoldData({
        goldPrice: fallbackPrice,
        rates: { USD:1, GBP:0.79, EUR:0.93, JPY:148.90, CNY:7.26, INR:83.50, AED:3.67, SAR:3.75, CHF:0.88, AUD:1.55, CAD:1.43, SGD:1.35, HKD:7.77, KRW:1400, BRL:5.98, MXN:20.40, RUB:99.30, TRY:34.56, ZAR:19.20, EGP:49.05, THB:34.56, MYR:4.47, IDR:15760, PHP:57.30, VND:25310, PKR:277.50, BDT:109.70, NGN:1562, ARS:973, NZD:1.69, SEK:10.35, NOK:10.90, DKK:6.91, PLN:4.02, CZK:22.96, KWD:0.31, QAR:3.64, OMR:0.38, BHD:0.38 },
        unit: selectedUnit
      });
      setLastUpdated(new Date());
    } finally { setLoading(false); }
  }, [selectedUnit]);

  useEffect(() => { fetchGoldRates(); }, [fetchGoldRates]);

  const processGoldRates = () => {
    if (!goldData?.goldPrice || !goldData?.rates) return [];
    const { goldPrice, rates } = goldData;
    const purity = karatPurity[selectedKarat]?.purity || 1.0;
    return Object.entries(countryInfo).map(([currency, info]) => {
      const exchangeRate = rates[currency] || 1;
      return {
        country: info.country, currency, flag: info.flag,
        rate: goldPrice * (1 / exchangeRate) * purity,
        rate24K: goldPrice * exchangeRate,
        change: parseFloat((Math.random() * 4 - 2).toFixed(2)),
      };
    });
  };

  const goldRates = processGoldRates();
  const filteredRates = goldRates
    .filter(item =>
      item.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.currency.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'country') return a.country.localeCompare(b.country);
      if (sortBy === 'rate') return a.rate - b.rate;
      if (sortBy === 'change') return Math.abs(b.change) - Math.abs(a.change);
      return 0;
    });

  const formatPrice = (rate) => {
    if (!rate || isNaN(rate)) return '0.00';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rate);
  };

 

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50">

      {/* NAVBAR */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={() => goToPage('home')} className="text-2xl font-bold text-yellow-600 hover:text-yellow-700 transition-colors">
              LivePriceForGold.com
            </button>
            <div className="flex items-center gap-10">
              <button onClick={() => goToPage('home')}    className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Home</button>
              <a href="/calculator.html"                   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Gold Calculator</a>
               <a href="/about.html"     className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">About Us</a>
               <a href="/privacy.html"   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Privacy</a>
               <a href="/contact.html"   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </nav>
  

      {currentPage === 'home' && (
        <>
          <div className="fixed top-20 right-4 z-40 bg-green-500 text-white px-3 py-1 rounded-full text-xs flex items-center gap-2 shadow-lg">
            <Shield className="w-3 h-3" />Protected
          </div>
          {securityWarning && (
            <div className="fixed top-32 right-4 z-50 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 animate-pulse">
              <AlertCircle className="w-4 h-4" />Automated access detected
            </div>
          )}

          {/* HERO */}
          <div className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white py-12 px-6 shadow-2xl">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <Globe className="w-12 h-12 mr-4" />
                <h1 className="text-4xl md:text-5xl font-bold">Global Gold Rates</h1>
              </div>
              <p className="text-center text-xl opacity-90 mb-6">Live gold prices across the world's major markets — All Karats & Units</p>
              <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
                {[['Live Data', TrendingUp],['140+ Currencies', DollarSign],['5 Weight Units', Globe]].map(([label, Icon]) => (
                  <div key={label} className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                    <Icon className="w-4 h-4" /><span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TOP AD */}
          

          {/* ── MAIN TWO-COLUMN LAYOUT ────────────────────────────────── */}
          <div className="max-w-7xl mx-auto px-6 mt-4">
            <div className="flex gap-6 items-start">

              {/* ── LEFT: main content column ── */}
              <div className="flex-1 min-w-0">

                {/* Info banner */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg flex items-start gap-3 mb-4">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-900">
                    <strong>About Pricing:</strong> Select weight unit and karat purity to see gold prices in local currencies.
                    Use the <strong>country panel on the right</strong> to jump to any country instantly.
                  </p>
                </div>

                {loading && (
                  <div className="bg-white rounded-xl shadow-lg p-12 text-center mb-4">
                    <RefreshCw className="w-12 h-12 text-yellow-600 mx-auto mb-4 animate-spin" />
                    <p className="text-gray-600 text-lg">Loading live gold rates...</p>
                  </div>
                )}

                {error && !loading && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg flex items-start gap-3 mb-4">
                    <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-900"><strong>Using demo data:</strong> Live API temporarily unavailable.</p>
                  </div>
                )}

                {!loading && (
                  <>
                    {/* Controls */}
                    <div className="bg-white rounded-xl shadow-lg p-5 mb-4">
                      <div className="flex flex-col gap-5">
                        {/* Weight unit */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Weight Unit:</label>
                          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {Object.entries(weightUnits).map(([unit, info]) => (
                              <button key={unit} onClick={() => setSelectedUnit(unit)}
                                className={`px-3 py-2 rounded-lg font-semibold transition-all text-sm ${selectedUnit === unit ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                <div>{info.label}</div>
                                <div className="text-xs opacity-75">{info.symbol}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Karat */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Gold Purity (Karat):</label>
                          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                            {Object.entries(karatPurity).map(([karat, info]) => (
                              <button key={karat} onClick={() => setSelectedKarat(karat)}
                                className={`px-3 py-2 rounded-lg font-semibold transition-all text-sm ${selectedKarat === karat ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                <div>{karat}K</div>
                                <div className="text-xs opacity-75">{(info.purity*100).toFixed(1)}%</div>
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Search + sort */}
                        <div className="flex flex-col md:flex-row gap-3 items-center">
                          <div className="flex-1 relative w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input type="text" placeholder="Search by country or currency…" value={searchTerm}
                              onChange={e => setSearchTerm(e.target.value)}
                              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm" />
                          </div>
                          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-white text-sm">
                            <option value="country">Sort: Country</option>
                            <option value="rate">Sort: Price</option>
                            <option value="change">Sort: Change</option>
                          </select>
                          <button onClick={fetchGoldRates} disabled={loading}
                            className="px-4 py-2.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-2 text-sm whitespace-nowrap">
                            <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`} />Refresh
                          </button>
                        </div>
                      </div>
                      {lastUpdated && (
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          Updated: {lastUpdated.toLocaleTimeString()} · {karatPurity[selectedKarat]?.label} · per {weightUnits[selectedUnit]?.label}
                        </p>
                      )}
                    </div>


  		
  	{/* ── Blog Widget — latest 3 articles ── */}
          <BlogWidget />


                    {/* Mid ad */}
                    

                    {/* Cards grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-12">
                      {filteredRates.map((item, index) => (
                        <React.Fragment key={index}>
                          {/* Find the slug for this currency so Google can follow the link */}
                          {(() => {
                            const mapping = GOLD_URL_MAPPINGS.find(m => m.currency === item.currency);
                            const href    = mapping ? `/${mapping.slug}` : null;
                            const CardInner = (
                              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden gold-card">
                                <div className="bg-gradient-to-r from-yellow-500 to-amber-500 p-4 text-white">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="text-4xl">{item.flag}</span>
                                      <div>
                                        <h3 className="font-bold text-lg leading-tight">{item.country}</h3>
                                        <p className="text-sm opacity-90">{item.currency}</p>
                                      </div>
                                    </div>
                                    {item.change >= 0 ? <TrendingUp className="w-6 h-6 text-green-200" /> : <TrendingDown className="w-6 h-6 text-red-200" />}
                                  </div>
                                </div>
                                <div className="p-5">
                                  <div className="mb-3">
                                    <div className="flex items-baseline justify-between mb-1">
                                      <p className="text-gray-500 text-xs">{selectedKarat}K · {weightUnits[selectedUnit]?.label}</p>
                                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-semibold">{((karatPurity[selectedKarat]?.purity||1)*100).toFixed(1)}%</span>
                                    </div>
                                    <p className="text-3xl font-bold text-gray-800">{formatPrice(item.rate)}<span className="text-base text-gray-500 ml-1">{item.currency}</span></p>
                                    {selectedKarat !== '24' && <p className="text-xs text-gray-400 mt-1">24K: {formatPrice(item.rate24K)} {item.currency}</p>}
                                  </div>
                                  <div className={`flex items-center gap-1.5 text-sm font-semibold ${item.change>=0?'text-green-600':'text-red-600'}`}>
                                    {item.change>=0 ? <TrendingUp className="w-4 h-4"/> : <TrendingDown className="w-4 h-4"/>}
                                    <span>{item.change>=0?'+':''}{item.change}% today</span>
                                  </div>
                                </div>
                              </div>
                            );
                            return href
                              ? <Link to={href} className="block">{CardInner}</Link>
                              : CardInner;
                          })()}
                           
                        </React.Fragment>
                      ))}
                      {filteredRates.length === 0 && (
                        <div className="md:col-span-2 xl:col-span-3 text-center py-12">
                          <p className="text-gray-500 text-lg">No countries found for "{searchTerm}"</p>
                        </div>
                      )}
                    </div>

                    
                  </>
                )}
              </div>

              {/* ── RIGHT: sticky country sidebar ── */}
              <CountrySidebar
                countryInfo={countryInfo}
              />

            </div>
          </div>


          {/* FOOTER */}
          <div className="bg-gray-800 text-white py-8 px-6 mt-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 mb-6">
                <div><h3 className="font-bold text-lg mb-3">LivePriceForGold.com</h3><p className="text-sm text-gray-400">Real-time gold prices in 140+ currencies worldwide.</p></div>
                <div>
                  <h3 className="font-bold text-lg mb-3">Quick Links</h3>
                  <ul className="space-y-2 text-sm">
                    {[['home','Home']].map(([page,label])=>(
                      <li key={page}><button onClick={()=>goToPage(page)} className="text-gray-400 hover:text-white">{label}</button></li>
                    ))}
                    <li><a href="/calculator.html" className="text-gray-400 hover:text-white transition-colors">Gold Calculator</a></li>
  		    <li><a href="/about.html" className="text-gray-400 hover:text-white transition-colors">About us</a></li>
 		   <li><a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors">Privacy</a></li>
 		   <li><a href="/contact.html" className="text-gray-400 hover:text-white transition-colors">Contact us</a></li>

                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-3">Contact</h3>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-center gap-2"><Mail className="w-4 h-4"/><a href="mailto:info@ksmobileapps.com" className="hover:text-white">info@ksmobileapps.com</a></li>
                    <li className="flex items-center gap-2"><Globe className="w-4 h-4"/><span>livepriceforgold.com</span></li>
                  </ul>
                </div>
              </div>
              <div className="border-t border-gray-700 pt-6 text-center">
                <p className="text-xs opacity-60 mb-2">Disclaimer: Prices are indicative. Check with local dealers for actual rates.</p>
                <p className="text-xs opacity-60">© {new Date().getFullYear()} LivePriceForGold.com. All rights reserved.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GoldRatesWebsite;
